import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { 
  TrendingUp, BarChart3, Briefcase, Settings, LogOut, 
  Brain, Loader2, ChevronRight, Target, AlertTriangle,
  TrendingDown, Award, Clock, Sparkles, Shield, Crown, HelpCircle, User
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import ReactMarkdown from 'react-markdown';
import { TraderProfileOnboarding } from '../components/TraderProfileOnboarding';
import { OverallScoreDisplay, SkillScoreGrid, SkillHistoryChart } from '../components/SkillScoreDisplay';
import Sidebar from '../components/Sidebar';

import API from '../utils/api';

// Helper function to get rank from score
const getRankFromScore = (score) => {
  if (score >= 1800) return 'Elite';
  if (score >= 1600) return 'Experte';
  if (score >= 1400) return 'Professionell';
  if (score >= 1200) return 'Fortgeschritten';
  if (score >= 1000) return 'Stabil';
  return 'Einsteiger';
};

const MentorPage = () => {
  const { t, language } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [noNewTrades, setNoNewTrades] = useState(null);  // New state for "no new trades" info
  
  // Strategy filter
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState('all');
  
  // Trader profile and skill scores
  const [traderProfile, setTraderProfile] = useState(null);
  const [showProfileOnboarding, setShowProfileOnboarding] = useState(false);
  const [skillScores, setSkillScores] = useState(null);
  const [scoreHistory, setScoreHistory] = useState([]);

  // Check if user has premium access
  const hasPremiumAccess = user?.is_admin === true || user?.subscription_plan === 'premium';

  // Fetch strategies
  const fetchStrategies = useCallback(async () => {
    try {
      const response = await fetch(`${API}/strategies`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setStrategies(data.strategies || []);
      }
    } catch (error) {
      console.error('Error fetching strategies:', error);
    }
  }, []);

  // Fetch trader profile
  const fetchTraderProfile = useCallback(async () => {
    try {
      const response = await fetch(`${API}/trader-profile`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTraderProfile(data);
      }
    } catch (error) {
      console.error('Error fetching trader profile:', error);
    }
  }, []);

  // Fetch skill scores
  const fetchSkillScores = useCallback(async (accountId) => {
    if (!accountId) return;
    try {
      // Fetch latest scores
      const latestRes = await fetch(`${API}/skill-scores/${accountId}/latest`, { credentials: 'include' });
      if (latestRes.ok) {
        const latestData = await latestRes.json();
        setSkillScores(latestData);
      }
      
      // Fetch history
      const historyRes = await fetch(`${API}/skill-scores/${accountId}`, { credentials: 'include' });
      if (historyRes.ok) {
        const historyData = await historyRes.json();
        setScoreHistory(historyData);
      }
    } catch (error) {
      console.error('Error fetching skill scores:', error);
    }
  }, []);

  // Fetch accounts
  const fetchAccounts = useCallback(async () => {
    try {
      const response = await fetch(`${API}/accounts`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAccounts(data);
        if (data.length > 0 && !selectedAccount) {
          setSelectedAccount(data[0].account_id);
        }
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  }, [selectedAccount]);

  // Fetch history
  const fetchHistory = useCallback(async () => {
    if (!selectedAccount) return;
    try {
      const response = await fetch(`${API}/mentor/history/${selectedAccount}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  }, [selectedAccount]);

  useEffect(() => {
    fetchAccounts();
    fetchTraderProfile();
    fetchStrategies();
  }, [fetchAccounts, fetchTraderProfile, fetchStrategies]);

  useEffect(() => {
    fetchHistory();
    if (selectedAccount) {
      fetchSkillScores(selectedAccount);
    }
  }, [fetchHistory, selectedAccount, fetchSkillScores]);

  // Show profile onboarding if no profile exists and user is premium
  useEffect(() => {
    if (user && hasPremiumAccess && traderProfile === null) {
      // Wait a bit to check if profile exists
      const timer = setTimeout(() => {
        if (traderProfile === null) {
          setShowProfileOnboarding(true);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [user, hasPremiumAccess, traderProfile]);

  // Show upgrade modal if not premium on mount
  useEffect(() => {
    if (user && !hasPremiumAccess) {
      setShowUpgradeModal(true);
    }
  }, [user, hasPremiumAccess]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleProfileComplete = (profile) => {
    setTraderProfile(profile);
  };

  const handleAnalyze = async () => {
    // Check premium access before analyzing
    if (!hasPremiumAccess) {
      setShowUpgradeModal(true);
      return;
    }

    if (!selectedAccount) return;
    setLoading(true);
    setAnalysis(null);
    setStats(null);
    setNoNewTrades(null);
    
    try {
      const requestBody = {
        account_id: selectedAccount,
        period: period,
      };
      
      if (period === 'month') {
        requestBody.month = selectedMonth;
        requestBody.year = selectedYear;
      }
      
      // Add strategy filter if selected (not "all")
      if (selectedStrategy && selectedStrategy !== 'all') {
        requestBody.strategy_id = selectedStrategy;
      }
      
      const response = await fetch(`${API}/mentor/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Check if no new trades to analyze
        if (data.no_new_trades) {
          setNoNewTrades({
            message: data.message,
            totalTrades: data.total_trades,
            lastAnalysisDate: data.last_analysis_date
          });
          return;
        }
        
        // Clear no new trades state if we got analysis
        setNoNewTrades(null);
        setAnalysis(data.analysis);
        setStats(data.stats);
        
        // Update skill scores if returned
        if (data.skill_scores) {
          setSkillScores(data.skill_scores);
          // Refresh score history
          fetchSkillScores(selectedAccount);
        }
        
        fetchHistory();
      }
    } catch (error) {
      console.error('Analysis error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Force re-analyze all trades
  const handleForceReanalyze = async () => {
    if (!hasPremiumAccess || !selectedAccount) return;
    
    setLoading(true);
    setAnalysis(null);
    setStats(null);
    setNoNewTrades(null);
    
    try {
      const requestBody = {
        account_id: selectedAccount,
        period: period,
        force_reanalyze: true
      };
      
      if (period === 'month') {
        requestBody.month = selectedMonth;
        requestBody.year = selectedYear;
      }
      
      const response = await fetch(`${API}/mentor/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });
      
      if (response.ok) {
        const data = await response.json();
        setNoNewTrades(null);
        setAnalysis(data.analysis);
        setStats(data.stats);
        
        if (data.skill_scores) {
          setSkillScores(data.skill_scores);
          fetchSkillScores(selectedAccount);
        }
        
        fetchHistory();
      }
    } catch (error) {
      console.error('Force reanalyze error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadHistoricalAnalysis = (item) => {
    setAnalysis(item.analysis);
    setStats(item.stats);
    if (item.skill_scores) {
      setSkillScores(item.skill_scores);
    }
  };

  const months = language === 'de' 
    ? ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember']
    : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background flex">
        {/* Sidebar */}
        <Sidebar user={user} onLogout={handleLogout} />

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500/20 to-blue-500/20 border border-emerald-500/30">
                  <Brain className="h-8 w-8 text-emerald-400" />
                </div>
                <div>
                  <h1 className="text-3xl font-heading font-bold">KI Trading Mentor</h1>
                  <p className="text-muted-foreground">
                    {language === 'de' 
                      ? 'Dein persönlicher Trading-Coach analysiert Performance & Psychologie'
                      : 'Your personal trading coach analyzes performance & psychology'}
                  </p>
                </div>
              </div>
            </div>

            {/* Controls */}
            <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
              <CardContent className="pt-6">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-sm text-muted-foreground mb-2 block">
                      {language === 'de' ? 'Account auswählen' : 'Select Account'}
                    </label>
                    <Select value={selectedAccount || ''} onValueChange={setSelectedAccount}>
                      <SelectTrigger data-testid="mentor-account-select">
                        <SelectValue placeholder={t('selectAccount')} />
                      </SelectTrigger>
                      <SelectContent>
                        {accounts.map((acc) => (
                          <SelectItem key={acc.account_id} value={acc.account_id}>
                            {acc.name} ({acc.broker || 'N/A'})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="min-w-[150px]">
                    <label className="text-sm text-muted-foreground mb-2 block">
                      {language === 'de' ? 'Zeitraum' : 'Period'}
                    </label>
                    <Select value={period} onValueChange={setPeriod}>
                      <SelectTrigger data-testid="mentor-period-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{language === 'de' ? 'Alle Trades' : 'All Trades'}</SelectItem>
                        <SelectItem value="month">{language === 'de' ? 'Monat' : 'Month'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Strategy Filter */}
                  {strategies.length > 0 && (
                    <div className="min-w-[180px]">
                      <label className="text-sm text-muted-foreground mb-2 block">
                        {language === 'de' ? 'Strategie' : 'Strategy'}
                      </label>
                      <Select value={selectedStrategy} onValueChange={setSelectedStrategy}>
                        <SelectTrigger data-testid="mentor-strategy-select">
                          <SelectValue placeholder={language === 'de' ? 'Alle Strategien' : 'All Strategies'} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{language === 'de' ? 'Alle Strategien' : 'All Strategies'}</SelectItem>
                          {strategies.map((s) => (
                            <SelectItem key={s.strategy_id} value={s.strategy_id}>
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                                {s.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {period === 'month' && (
                    <>
                      <div className="min-w-[140px]">
                        <label className="text-sm text-muted-foreground mb-2 block">
                          {language === 'de' ? 'Monat' : 'Month'}
                        </label>
                        <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {months.map((m, i) => (
                              <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="min-w-[100px]">
                        <label className="text-sm text-muted-foreground mb-2 block">
                          {language === 'de' ? 'Jahr' : 'Year'}
                        </label>
                        <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[2024, 2025, 2026].map((y) => (
                              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                  
                  <Button 
                    onClick={handleAnalyze} 
                    disabled={loading || !selectedAccount}
                    className="glow-primary min-w-[180px]"
                    data-testid="start-analysis-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {language === 'de' ? 'Analysiere...' : 'Analyzing...'}
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        {language === 'de' ? 'Analyse starten' : 'Start Analysis'}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Trader Profile Badge (if exists) */}
            {traderProfile && (
              <div className="mb-6 flex items-center gap-4">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-800/50 border border-zinc-700">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {traderProfile.trading_style === 'daytrader' ? 'Daytrader' : traderProfile.trading_style === 'swing' ? 'Swing Trader' : 'Position Trader'}
                  </span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowProfileOnboarding(true)}>
                  {language === 'de' ? 'Profil bearbeiten' : 'Edit Profile'}
                </Button>
              </div>
            )}

            {/* Skill Scores Display (always show for premium users) */}
            {hasPremiumAccess && skillScores && (
              <div className="mb-6 space-y-4">
                <OverallScoreDisplay 
                  score={skillScores.overall_score} 
                  rank={skillScores.rank || getRankFromScore(skillScores.overall_score)}
                  confidence={skillScores.confidence_level}
                  isDefault={skillScores.is_default}
                  scores={skillScores}
                />
                <SkillScoreGrid 
                  scores={skillScores} 
                  deltas={skillScores.deltas}
                  reasoning={skillScores.reasoning}
                />
                {scoreHistory.length > 1 && (
                  <SkillHistoryChart history={scoreHistory} />
                )}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Main Analysis Area */}
              <div className="lg:col-span-3">
                {/* Stats Cards */}
                {stats && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <Card className="bg-zinc-900/50 border-zinc-800">
                      <CardContent className="pt-4 pb-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Target className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Win-Rate</span>
                        </div>
                        <p className={`text-2xl font-mono font-bold ${stats.win_rate >= 50 ? 'text-emerald-400' : 'text-amber-400'}`}>
                          {Number(stats.win_rate).toFixed(2)}%
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="bg-zinc-900/50 border-zinc-800">
                      <CardContent className="pt-4 pb-4">
                        <div className="flex items-center gap-2 mb-1">
                          <TrendingUp className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Profit-Faktor</span>
                        </div>
                        <p className={`text-2xl font-mono font-bold ${stats.profit_factor >= 1.5 ? 'text-emerald-400' : stats.profit_factor >= 1 ? 'text-amber-400' : 'text-rose-400'}`}>
                          {Number(stats.profit_factor).toFixed(2)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="bg-zinc-900/50 border-zinc-800">
                      <CardContent className="pt-4 pb-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Award className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Gesamt P&L</span>
                        </div>
                        <p className={`text-2xl font-mono font-bold ${stats.total_pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {stats.total_pnl >= 0 ? '+' : ''}{Number(stats.total_pnl).toFixed(2)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="bg-zinc-900/50 border-zinc-800">
                      <CardContent className="pt-4 pb-4">
                        <div className="flex items-center gap-2 mb-1">
                          <BarChart3 className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Trades</span>
                        </div>
                        <p className="text-2xl font-mono font-bold text-white">
                          {stats.win_count}<span className="text-emerald-400">/</span>{stats.loss_count}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Analysis Result */}
                {analysis ? (
                  <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Brain className="h-5 w-5 text-emerald-400" />
                        {language === 'de' ? 'Mentor-Analyse' : 'Mentor Analysis'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="prose prose-invert prose-sm max-w-none">
                        <ReactMarkdown
                          components={{
                            h1: ({node, ...props}) => <h1 className="text-2xl font-bold text-white mt-6 mb-4 border-b border-zinc-700 pb-2" {...props} />,
                            h2: ({node, ...props}) => <h2 className="text-xl font-bold text-emerald-400 mt-6 mb-3" {...props} />,
                            h3: ({node, ...props}) => <h3 className="text-lg font-semibold text-white mt-4 mb-2" {...props} />,
                            p: ({node, ...props}) => <p className="text-zinc-300 mb-3 leading-relaxed" {...props} />,
                            ul: ({node, ...props}) => <ul className="list-disc list-inside space-y-1 mb-4 text-zinc-300" {...props} />,
                            ol: ({node, ...props}) => <ol className="list-decimal list-inside space-y-1 mb-4 text-zinc-300" {...props} />,
                            li: ({node, ...props}) => <li className="text-zinc-300" {...props} />,
                            strong: ({node, ...props}) => <strong className="text-white font-semibold" {...props} />,
                            em: ({node, ...props}) => <em className="text-emerald-300" {...props} />,
                            blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-emerald-500 pl-4 py-2 my-4 bg-emerald-500/10 rounded-r-lg" {...props} />,
                            code: ({node, ...props}) => <code className="bg-zinc-800 px-1.5 py-0.5 rounded text-emerald-300 text-sm" {...props} />,
                          }}
                        >
                          {analysis}
                        </ReactMarkdown>
                      </div>
                    </CardContent>
                  </Card>
                ) : noNewTrades ? (
                  // No new trades to analyze
                  <Card className="bg-zinc-900/50 border-zinc-800 border-amber-500/30">
                    <CardContent className="py-12 text-center">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/10 flex items-center justify-center">
                        <AlertTriangle className="h-8 w-8 text-amber-400" />
                      </div>
                      <h3 className="text-lg font-medium mb-2 text-amber-400">
                        {language === 'de' ? 'Keine neuen Trades' : 'No New Trades'}
                      </h3>
                      <p className="text-muted-foreground max-w-md mx-auto mb-4">
                        {noNewTrades.message}
                      </p>
                      {noNewTrades.lastAnalysisDate && (
                        <p className="text-sm text-muted-foreground mb-6">
                          {language === 'de' 
                            ? `Letzte Analyse: ${noNewTrades.lastAnalysisDate}`
                            : `Last analysis: ${noNewTrades.lastAnalysisDate}`}
                        </p>
                      )}
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button
                          variant="outline"
                          onClick={handleForceReanalyze}
                          disabled={loading}
                          className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                        >
                          {loading ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Sparkles className="h-4 w-4 mr-2" />
                          )}
                          {language === 'de' ? 'Trotzdem neu analysieren' : 'Re-analyze anyway'}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-4">
                        {language === 'de' 
                          ? 'Füge neue Trades hinzu, um eine frische Analyse zu erhalten.'
                          : 'Add new trades to get a fresh analysis.'}
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="py-16 text-center">
                      {loading ? (
                        <div className="space-y-4">
                          <div className="relative w-20 h-20 mx-auto">
                            <div className="absolute inset-0 rounded-full border-4 border-zinc-700"></div>
                            <div className="absolute inset-0 rounded-full border-4 border-t-emerald-500 animate-spin"></div>
                            <Brain className="absolute inset-0 m-auto h-8 w-8 text-emerald-400" />
                          </div>
                          <p className="text-muted-foreground">
                            {language === 'de' 
                              ? 'KI analysiert dein Trading-Journal...'
                              : 'AI is analyzing your trading journal...'}
                          </p>
                        </div>
                      ) : (
                        <>
                          <Brain className="h-16 w-16 mx-auto mb-4 text-zinc-600" />
                          <h3 className="text-lg font-medium mb-2">
                            {language === 'de' ? 'Bereit für die Analyse' : 'Ready for Analysis'}
                          </h3>
                          <p className="text-muted-foreground max-w-md mx-auto">
                            {language === 'de' 
                              ? 'Wähle einen Account und Zeitraum aus, dann klicke auf "Analyse starten" um deine personalisierte Mentor-Auswertung zu erhalten.'
                              : 'Select an account and period, then click "Start Analysis" to get your personalized mentor evaluation.'}
                          </p>
                        </>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* History Sidebar */}
              <div className="lg:col-span-1">
                <Card className="bg-zinc-900/50 border-zinc-800 sticky top-6">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      {language === 'de' ? 'Frühere Analysen' : 'Previous Analyses'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {history.length > 0 ? (
                      history.slice(0, 10).map((item, index) => (
                        <button
                          key={item.analysis_id || index}
                          onClick={() => loadHistoricalAnalysis(item)}
                          className="w-full text-left p-3 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 transition-colors group"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-zinc-300">
                                {item.period === 'month' 
                                  ? `${months[(item.stats?.month || 1) - 1]} ${item.stats?.year || ''}`
                                  : (language === 'de' ? 'Alle Trades' : 'All Trades')}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(item.created_at).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US')}
                              </p>
                            </div>
                            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-white transition-colors" />
                          </div>
                          {item.stats && (
                            <div className="flex gap-3 mt-2 text-xs">
                              <span className={item.stats.total_pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                                {item.stats.total_pnl >= 0 ? '+' : ''}{Number(item.stats.total_pnl).toFixed(2)}
                              </span>
                              <span className="text-muted-foreground">
                                WR: {Number(item.stats.win_rate).toFixed(2)}%
                              </span>
                            </div>
                          )}
                        </button>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        {language === 'de' ? 'Noch keine Analysen' : 'No analyses yet'}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </main>

        {/* Upgrade Modal for Non-Premium Users */}
        <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
          <DialogContent className="glass max-w-md">
            <DialogHeader>
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30">
                <Crown className="h-8 w-8 text-purple-400" />
              </div>
              <DialogTitle className="text-center text-2xl">
                {language === 'de' ? 'Premium Feature' : 'Premium Feature'}
              </DialogTitle>
              <DialogDescription className="text-center">
                {language === 'de' 
                  ? 'Der KI Trading Mentor ist exklusiv für Premium-Mitglieder verfügbar. Upgraden Sie jetzt, um personalisierte Trading-Analysen und Coaching zu erhalten.'
                  : 'The AI Trading Mentor is exclusively available for Premium members. Upgrade now to get personalized trading analysis and coaching.'}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-3">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50">
                <Target className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                <p className="text-sm">
                  {language === 'de' ? 'Detaillierte Performance-Analyse' : 'Detailed performance analysis'}
                </p>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50">
                <Brain className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                <p className="text-sm">
                  {language === 'de' ? 'KI-gestütztes Trading-Coaching' : 'AI-powered trading coaching'}
                </p>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50">
                <Award className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                <p className="text-sm">
                  {language === 'de' ? 'Psychologische Insights' : 'Psychological insights'}
                </p>
              </div>
            </div>
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setShowUpgradeModal(false)} className="flex-1">
                {language === 'de' ? 'Später' : 'Later'}
              </Button>
              <Button onClick={() => navigate('/subscription')} className="flex-1 glow-primary">
                <Crown className="h-4 w-4 mr-2" />
                {language === 'de' ? 'Jetzt upgraden' : 'Upgrade now'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Trader Profile Onboarding Modal */}
        <TraderProfileOnboarding 
          open={showProfileOnboarding} 
          onOpenChange={setShowProfileOnboarding}
          onComplete={handleProfileComplete}
        />
      </div>
    </TooltipProvider>
  );
};

export default MentorPage;
